<script setup lang="ts">
import { getFormQuestionComponent, generateFormSchema } from '~/utils/generateFormSchema'

const props = defineProps<{
  token?: string
  form: Form & { formQuestions: FormQuestion[] }
  initialValues?: Record<string, any>
  size?: 'md' | 'lg'
  align?: 'left' | 'center'
}>()

const emit = defineEmits<{
  (e: 'submit', values: Record<string, any>): void
}>()

const schema = computed(() => generateFormSchema(props.form.formQuestions))

const { handleSubmit, isSubmitting, values } = useForm({
  validationSchema: toTypedSchema(schema.value),
  initialValues: props.initialValues ?? {},
})

const { handleServerError } = useServerErrorHandler()
const { handleValidationError } = useFormValidationErrorHandler()

const onSubmit = handleSubmit(async (values) => {
  try {
    const payload = {
      form_question_responses: Object.entries(values).map(([key, value]: [string, any]) => ({
        form_question_id: key.replace('question_', ''),
        value: value,
      })),
    }

    let submission
    if (props.token) {
      submission = await submitFormSubmission(props.token, payload)
    }
    else {
      submission = await submitPublicForm(props.form.identifier, payload)
    }

    emit('submit', values)
  }
  catch (error) {
    console.error('Error submitting form', error)
    handleServerError(error)
  }
}, handleValidationError)

</script>

<template>
  <FormCard
    :title="form.title"
    :sub-title="form.description"
    :is-submitting="isSubmitting"
    :show-cancel-button="false"
    :size="size"
    :align="align"
    @submit.prevent="onSubmit"
  >
    <template
      v-for="question in form.formQuestions"
      :key="question.id"
    >
      <component
        :is="getFormQuestionComponent(question)"
        :name="`question_${question.id}`"
        :label="question.text"
        :type="question.questionType"
        :required="question.required"
        :items="question.options"
      />
    </template>
  </FormCard>
</template>

<i18n lang="json">
{
  "en": {
    "": ""
  },
  "nl": {
    "": ""
  }
}
</i18n>
